import {
  EQ_GAIN_BANDS,
  HPF_PRESETS,
  LPF_PRESETS,
  eqGainDb,
  formatHz,
  hpfHz,
  lpfHz,
  valueFromHpfHz,
  valueFromLpfHz,
  type InstrumentFilters,
} from "../lib/dials";

type EqStripProps = {
  filters: InstrumentFilters;
  onChange: (next: InstrumentFilters) => void;
  label: string;
};

export function EqStrip({ filters, onChange, label }: EqStripProps) {
  const setBand = (id: keyof InstrumentFilters, value: number) => {
    onChange({ ...filters, [id]: value });
  };

  return (
    <div className="mf-eq-strip" aria-label={label}>
      <div className="mf-eq-bars" aria-hidden="true">
        {EQ_GAIN_BANDS.map((b) => (
          <span
            key={b.id}
            title={b.label}
            style={{ height: `${Math.max(6, Math.round((filters[b.id] / 100) * 40))}px` }}
          />
        ))}
      </div>
      <div className="mf-chips" aria-label={`${label} high-pass and low-pass`}>
        <span className="mf-label">HPF</span>
        {HPF_PRESETS.map((hz) => (
          <button
            key={`hpf-${hz}`}
            type="button"
            className={(hz === 0 ? filters.hpf <= 4 : filters.hpf === valueFromHpfHz(hz)) ? "on" : ""}
            onClick={() => setBand("hpf", hz === 0 ? 0 : valueFromHpfHz(hz))}
          >
            {hz === 0 ? "Off" : `${hz} Hz`}
          </button>
        ))}
        <span className="mf-label">LPF</span>
        {LPF_PRESETS.map((hz) => (
          <button
            key={`lpf-${hz}`}
            type="button"
            className={(hz >= 20000 ? filters.lpf <= 4 : filters.lpf === valueFromLpfHz(hz)) ? "on" : ""}
            onClick={() => setBand("lpf", hz >= 20000 ? 0 : valueFromLpfHz(hz))}
          >
            {hz >= 20000 ? "Open" : `${Math.round(hz / 1000)}k`}
          </button>
        ))}
      </div>
      <section className="mf-dials">
        <div className="mf-dial-row">
          <span>High-pass</span>
          <input
            type="range"
            min={0}
            max={100}
            value={filters.hpf}
            aria-label={`${label} high-pass`}
            onChange={(e) => setBand("hpf", Number(e.target.value))}
          />
          <b>{hpfHz(filters.hpf)} Hz</b>
          <span />
        </div>
        {EQ_GAIN_BANDS.map((b) => {
          const db = eqGainDb(filters[b.id]);
          const dbLabel = db === 0 ? "0 dB" : db > 0 ? `+${db} dB` : `${db} dB`;
          return (
            <div className="mf-dial-row" key={b.id}>
              <span>
                {b.label} {formatHz(b.hz)}
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={filters[b.id]}
                aria-label={`${label} ${b.label}`}
                onChange={(e) => setBand(b.id, Number(e.target.value))}
              />
              <b>{dbLabel}</b>
              <span />
            </div>
          );
        })}
        <div className="mf-dial-row">
          <span>Low-pass</span>
          <input
            type="range"
            min={0}
            max={100}
            value={filters.lpf}
            aria-label={`${label} low-pass`}
            onChange={(e) => setBand("lpf", Number(e.target.value))}
          />
          <b>{lpfHz(filters.lpf)} Hz</b>
          <span />
        </div>
      </section>
    </div>
  );
}
